import type { AnaliseAdicionada } from '../types'

/** Item do grupo de análises de um laboratório (retorno de useGetGrupoAnalises). */
export interface AnaliseDoLaboratorio {
  analise_nome?: string
  profundidade_nome?: string
  valor?: string | number
  cod_analise?: number
  [key: string]: any
}

export interface ResultadoReprecificacao {
  /** Análises que existem no novo laboratório, já com o valor atualizado. */
  encontradas: AnaliseAdicionada[]
  /** Análises que o novo laboratório não oferece — precisam ser descartadas. */
  nao_encontradas: AnaliseAdicionada[]
}

/** Normaliza para comparar nomes: sem acento, sem espaços extras, minúsculo. */
function normalizar(texto?: string | number): string {
  return String(texto ?? '')
    .normalize('NFD')
    // Remove os diacríticos separados pelo NFD (bloco Combining Diacritical Marks).
    .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')
    .trim()
    .toLowerCase()
}

/**
 * Converte para Number aceitando os dois formatos que circulam aqui: o valor cru
 * do backend, com ponto decimal ("15.50"), e o formatado em BRL ("R$ 1.234,56").
 *
 * A vírgula é o desempate: quando ela existe, é o separador decimal e os pontos
 * são de milhar; sem vírgula, o ponto é o próprio decimal. Tratar todo ponto como
 * milhar transformaria "15.50" em 1550.
 */
function paraNumero(valor?: string | number): number {
  if (typeof valor === 'number') return isFinite(valor) ? valor : 0
  if (!valor) return 0

  const bruto = String(valor).replace(/[^\d.,-]/g, '')

  const limpo = bruto.includes(',')
    ? bruto.replace(/\./g, '').replace(',', '.')
    : bruto

  const n = parseFloat(limpo)
  return isNaN(n) || !isFinite(n) ? 0 : n
}

/** Formata um número como moeda BRL, no mesmo formato usado nos cards. */
function formatarBRL(valor: number): string {
  return valor.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
}

/**
 * Recalcula o valor das análises já adicionadas contra a tabela de preços de
 * outro laboratório.
 *
 * O casamento é por **profundidade + nome da análise** (não por cod: o mesmo
 * exame tem cod diferente em cada laboratório). Espelha a regra da versão em
 * produção (`ordemLaboratorio/index.tsx:1064-1084`), com a comparação
 * endurecida para ignorar acento e espaço além do caixa.
 *
 * O que o novo laboratório não oferece volta em `nao_encontradas` para o usuário
 * decidir — descartar essas análises ou cancelar a troca.
 */
export function reprecificarAnalises(
  analises: AnaliseAdicionada[],
  grupoDoLaboratorio: AnaliseDoLaboratorio[]
): ResultadoReprecificacao {
  const grupo = grupoDoLaboratorio ?? []

  const encontradas: AnaliseAdicionada[] = []
  const nao_encontradas: AnaliseAdicionada[] = []

  ;(analises ?? []).forEach((item) => {
    const correspondente = grupo.find(
      (opcao) =>
        normalizar(opcao?.profundidade_nome) === normalizar(item?.profundidade) &&
        normalizar(opcao?.analise_nome) === normalizar(item?.analise_nome)
    )

    if (!correspondente) {
      nao_encontradas.push(item)
      return
    }

    const valorUnitario = paraNumero(correspondente?.valor)
    const qtdPontos = item?.pontos_selecionados?.length ?? 0

    encontradas.push({
      ...item,
      // O cod da análise muda entre laboratórios — sem atualizar, o payload
      // apontaria para um exame que o novo laboratório não conhece.
      cod_analise: correspondente?.cod_analise ?? item?.cod_analise,
      valor: String(valorUnitario),
      valor_analise: formatarBRL(valorUnitario * qtdPontos),
    })
  })

  return { encontradas, nao_encontradas }
}
